import { api } from '@packages/backend/convex/_generated/api';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { signOut } from 'aws-amplify/auth';
import { useQuery } from 'convex-helpers/react';
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { ActionButton } from '@/components/ui+/ActionButton';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { routeTree } from '@/routeTree.gen';

const router = createRouter({
  // defaultErrorComponent: ({ error }) => <ErrorComponent error={error} />,
  context: {
    convexUser: { sub: null, userType: null },
    isAuthenticated: false,
  },
  defaultGcTime: 0,
  defaultPendingComponent: PageLoading,
  defaultPreload: false,
  routeTree,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Not sure if this is correct, but trying to get types to work.
// See https://tanstack.com/router/latest/docs/framework/react/guide/type-safety#avoid-internal-types-without-narrowing
// const props = {
//   to: '/posts/',
// } as const satisfies LinkProps<RegisteredRouter, string '/posts/'>

export type RegisteredRouter = typeof router;

export const AppRouter = () => {
  // IMPORTANT.
  // Any queries that are made throughout the app will be cached if by Convex Provider (only if they are defined here).
  // const menu = useQuery(api.menus.get, { restaurantUuid: '17abf563-ab88-43c1-83e9-c1d908deab14' });

  // const clientAuth = useAuth();
  const convexUserQuery = useQuery(api.auth.getCurrentUser);
  const isServerAuthenticated = convexUserQuery.isSuccess && convexUserQuery.data.userType !== null;
  const posthog = usePostHog();

  // Identify the user with PostHog
  useEffect(() => {
    if (convexUserQuery.data?.sub) {
      posthog.identify(convexUserQuery.data.sub, { userType: convexUserQuery.data.userType });
    }
  }, [convexUserQuery.data?.sub]);

  // Wait for auth to initialise before rendering the router
  if (convexUserQuery.isPending) return <PageLoading />;

  if (convexUserQuery.isError) {
    console.error(convexUserQuery.error);
    return (
      <div className={'p-4 grid gap-4'}>
        <div>There was an error loading the user.</div>
        <ActionButton className="w-[120px]" onClick={() => signOut()}>
          Sign out
        </ActionButton>
      </div>
    );
  }

  return (
    <RouterProvider
      context={{
        convexUser: convexUserQuery.data,
        isAuthenticated: isServerAuthenticated,
      }}
      defaultGcTime={0}
      defaultPreload={false}
      router={router}
    />
  );
};

function PageLoading() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <LoadingIcon />
    </div>
  );
}
